/**
 * The settings store — definitions, decode, encode.
 *
 * Storage is `ProfileData.settings`, the loose `Record<string, string | number
 * | boolean>` the save envelope already carries and already migrates. Nothing
 * new is invented: `codexThumbs` and `prologueSeen` live there for the same
 * reason, and a second storage mechanism would need its own migration path the
 * first time a value's shape changed.
 *
 * Two rules hold this file honest.
 *
 *   1. **Every definition names the thing it changes**, in `effect`, and the
 *      screen renders it under the row. A setting that cannot say what it does
 *      is a setting that does nothing, and this project has been burned by
 *      controls that looked live twice already.
 *   2. **Decode is total.** An unrecognised stored value falls back to the
 *      default rather than propagating a string nothing matches — the same
 *      argument `save.ts` makes about migrations never throwing.
 *
 * Deliberately absent, and why:
 *   - **Audio.** There is no audio in this project at all: no `AudioContext`,
 *     no sample bank, nothing to set a level on. A volume slider would be a
 *     slider.
 *   - **Movement and interface keys.** Rebinding lives here now, but not for
 *     these. A/D also live on the arrow keys, and Tab, Escape, C and M are read
 *     by their literal `e.code` inside `ui/uiRoot.ts` as well as by `Input`, so
 *     moving one would move half of it. `RESERVED_CODES` is that list, and the
 *     screen refuses to bind onto it.
 *
 * Pure: imports one constant, from `src/ds/tokens.ts`, which itself imports
 * nothing — and nothing from `src/core`, which is why the rebinding table below
 * holds action names as plain strings. Touches no DOM, and is the whole
 * testable surface of the settings screen.
 */

import { UI_FS_BASE_PX } from '../ds/tokens';

export type SettingId =
  | 'uiScale'
  | 'contrast'
  | 'screenShake'
  | 'ticker'
  | 'hints'
  | 'debugOverlay'
  | 'padDeadzone';

export interface SettingOption {
  value: string;
  label: string;
}

export interface SettingDef {
  id: SettingId;
  /** The key under which this lives in `ProfileData.settings`. */
  key: string;
  label: string;
  /** What it changes, concretely. Rendered under the row. */
  effect: string;
  options: readonly SettingOption[];
  default: string;
  /** Option value -> stored value. */
  encode(value: string): string | number | boolean;
  /** Stored value -> option value, or null when it matches no option. */
  decode(raw: string | number | boolean | undefined): string | null;
}

/** Plain string in, plain string out — the shape all but one setting uses. */
function plain(raw: string | number | boolean | undefined): string | null {
  return typeof raw === 'string' ? raw : null;
}

const ON_OFF: readonly SettingOption[] = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

export const SETTING_DEFS: readonly SettingDef[] = [
  {
    id: 'uiScale',
    key: 'ui.scale',
    label: 'Interface scale',
    effect:
      'Sets --ui-fs on #ui-root, and --ui-u derives from it: HUD, panels, toasts and every menu resize with it.',
    options: [
      { value: 'small', label: 'Small' },
      { value: 'normal', label: 'Normal' },
      { value: 'large', label: 'Large' },
      { value: 'huge', label: 'Huge' },
    ],
    default: 'normal',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'contrast',
    key: 'ui.contrast',
    label: 'High contrast',
    effect: 'Overrides the UI palette variables: brighter text, harder panel borders.',
    options: [
      { value: 'normal', label: 'Off' },
      { value: 'high', label: 'On' },
    ],
    default: 'normal',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'screenShake',
    key: 'ui.shake',
    label: 'Screen shake',
    effect: 'Multiplies Camera.shakeScale, which every trauma source in the run is scaled by.',
    options: [
      { value: 'full', label: 'Full' },
      { value: 'half', label: 'Half' },
      { value: 'off', label: 'Off' },
    ],
    default: 'full',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'ticker',
    key: 'ui.ticker',
    label: 'Event ticker',
    effect: 'Shows or hides the running event log at the top left of the screen.',
    options: ON_OFF,
    default: 'on',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'hints',
    key: 'ui.hints',
    label: 'Control hints',
    effect: 'Shows or hides the two-line key reminder across the top of the screen.',
    options: ON_OFF,
    default: 'on',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'debugOverlay',
    key: 'ui.debug',
    label: 'Debug overlay',
    effect: 'Starts a run with the F3 profiler overlay open. F3 still toggles it in play.',
    options: [
      { value: 'off', label: 'Off' },
      { value: 'on', label: 'On' },
    ],
    default: 'off',
    encode: (v) => v,
    decode: plain,
  },
  {
    id: 'padDeadzone',
    key: 'pad.deadzone',
    label: 'Stick deadzone',
    effect:
      "Sets the radius core/input.ts's pollGamepad() drops before rescaling the left stick " +
      "to reach 1.0, and the radius ui/menuPad.ts's stickDirection() drops before a stick " +
      'move counts in any menu. Higher swallows more centre drift; lower reaches full speed ' +
      'on a smaller push. Does not change 8-way aim (a separate, coarser threshold).',
    options: [
      { value: '0.10', label: '10% — tight' },
      { value: '0.16', label: '16%' },
      { value: '0.22', label: '22% — default' },
      { value: '0.28', label: '28%' },
      { value: '0.34', label: '34%' },
      { value: '0.40', label: '40% — loose' },
    ],
    // Mirrors `core/input.ts`'s `PAD_DEADZONE` — kept as a literal rather than
    // an import because this file is deliberately clean of `src/core`
    // (see the file header); `tests/settingsModel.test.ts` cross-checks the
    // two numbers so they cannot drift apart silently.
    default: '0.22',
    encode: (v) => v,
    decode: plain,
  },
  // The "play the prologue" toggle that lived here moved to the title screen
  // as a Prologue button — a setting that silently changed what New Run did
  // later was harder to find than a row that just runs the thing now.
  // `prologueSeen` is still the flag `Game`/`ui/scene.ts` read and write; only
  // this screen's control surface for it is gone.
];

const BY_ID = new Map<SettingId, SettingDef>(SETTING_DEFS.map((d) => [d.id, d]));

export function settingDef(id: SettingId): SettingDef {
  const d = BY_ID.get(id);
  if (!d) throw new Error(`unknown setting: ${id}`);
  return d;
}

export type SettingsState = Record<SettingId, string>;

export function defaultSettings(): SettingsState {
  const out = {} as SettingsState;
  for (const d of SETTING_DEFS) out[d.id] = d.default;
  return out;
}

/** Total: anything unrecognised becomes the default. */
export function readSettings(
  raw: Readonly<Record<string, string | number | boolean>>,
): SettingsState {
  const out = {} as SettingsState;
  for (const d of SETTING_DEFS) {
    const decoded = d.decode(raw[d.key]);
    out[d.id] = decoded !== null && d.options.some((o) => o.value === decoded)
      ? decoded
      : d.default;
  }
  return out;
}

/** Write in place, so unrelated keys (`codexThumbs`) survive untouched. */
export function writeSettings(
  state: Readonly<SettingsState>,
  into: Record<string, string | number | boolean>,
): void {
  for (const d of SETTING_DEFS) into[d.key] = d.encode(state[d.id]);
}

/** Next/previous option, wrapping. An unrecognised current value lands on index 0. */
export function cycleSetting(id: SettingId, current: string, dir: 1 | -1): string {
  const d = settingDef(id);
  const i = d.options.findIndex((o) => o.value === current);
  if (i < 0) return d.options[0].value;
  const n = (i + dir + d.options.length) % d.options.length;
  return d.options[n].value;
}

/** The `Camera.shakeScale` multiplier each option maps to. */
export function shakeScaleFor(value: string): number {
  switch (value) {
    case 'off':
      return 0;
    case 'half':
      return 0.5;
    default:
      return 1;
  }
}

/**
 * The number `core/input.ts`'s `setPadDeadzone` wants, from a `padDeadzone`
 * option value.
 *
 * The option values already *are* the number, as a string — there is no
 * separate label-to-number table to keep in sync, unlike `shakeScaleFor`'s
 * named options. `Number` on an unrecognised value falls back to the
 * definition's own default rather than `NaN`, so a corrupted stored value
 * that somehow slipped past `readSettings` still reaches `setPadDeadzone`
 * with something it can clamp instead of poisoning the poll.
 */
export function padDeadzoneFor(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number(settingDef('padDeadzone').default);
}

// ---------------------------------------------------------------- rebinding
//
// Key rebinding was left off this screen because `Input.rebind` could only add
// a mapping: moving jump to Z left Space jumping as well, which is not a
// rebind. `Input.unbind` and an exported `DEFAULT_BINDINGS` are what make it
// expressible, and `resolveBindings` below is where the "and the old key stops
// working" half actually happens.

export interface RebindDef {
  /**
   * An `Action` from `core/input.ts`, held as a plain string so this file keeps
   * importing nothing. A test asserts every one of these is a real action in
   * `DEFAULT_BINDINGS`, which is what stops the looseness becoming a lie.
   */
  action: string;
  label: string;
}

/**
 * Codes the interface layer reads by literal `e.code` in `ui/uiRoot.ts` as well
 * as `Input`, so a rebind of one of them would take in one place and not the
 * other. The screen refuses to bind onto them and offers no row for the actions
 * behind them.
 */
export const RESERVED_CODES: readonly string[] = ['Tab', 'Escape', 'KeyC', 'KeyM'];

/**
 * What can be moved.
 *
 * Movement is absent on purpose: `left` and `right` each hold two default codes
 * (WASD *and* the arrows) and `resolveBindings` clears every code an action
 * holds, so rebinding `left` would silently take the arrow key away too. An
 * option that costs you a key you did not name is worse than no option.
 */
export const REBINDABLE: readonly RebindDef[] = [
  { action: 'jump', label: 'Jump' },
  { action: 'dash', label: 'Dash' },
  { action: 'grip', label: 'Grip / wall-cling' },
  { action: 'mainAttack', label: 'Main attack' },
  { action: 'offAttack', label: 'Off attack' },
  { action: 'ability', label: 'Ability' },
  { action: 'parry', label: 'Parry' },
  { action: 'interact', label: 'Interact' },
  { action: 'swap', label: 'Swap hands' },
];

const REBINDABLE_ACTIONS = new Set(REBINDABLE.map((r) => r.action));

/** action -> the code the player chose for it. Absent means "the default". */
export type BindOverrides = Readonly<Record<string, string>>;

/** The `ProfileData.settings` key the overrides ride in. */
const BINDS_KEY = 'input.binds';

/** Codes are `KeyboardEvent.code` values: letters and digits, nothing exotic. */
const CODE_RE = /^[A-Za-z][A-Za-z0-9]*$/;

/**
 * Can this code be bound? The single judgement, asked by the screen before it
 * reports a rebind, by the writer before it stores one, and by the resolver
 * before it honours one.
 *
 * It is one function because it was briefly three. A keydown carrying no `code`
 * at all — which a synthetic event can — passed the screen's check, failed the
 * resolver's, and was written to the profile in between: the row said the key
 * had moved, the profile grew a line, and pressing the key did nothing. A check
 * that only some of the path performs is the same as no check.
 */
export function isBindableCode(code: string): boolean {
  return CODE_RE.test(code) && !RESERVED_CODES.includes(code);
}

/**
 * Total, in the same sense `readSettings` is: anything unparseable is dropped
 * rather than propagated. A stored override naming an action that has since
 * left `REBINDABLE` would otherwise resurface as a key nothing can undo,
 * because the screen would show no row for it.
 */
export function readBindOverrides(
  raw: Readonly<Record<string, string | number | boolean>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  const packed = raw[BINDS_KEY];
  if (typeof packed !== 'string') return out;
  for (const pair of packed.split(',')) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue;
    const action = pair.slice(0, eq);
    const code = pair.slice(eq + 1);
    if (!REBINDABLE_ACTIONS.has(action)) continue;
    if (!isBindableCode(code)) continue;
    out[action] = code;
  }
  return out;
}

/** Write in place, so unrelated profile keys survive — same rule as settings. */
export function writeBindOverrides(
  overrides: BindOverrides,
  into: Record<string, string | number | boolean>,
): void {
  into[BINDS_KEY] = Object.entries(overrides)
    .filter(([action, code]) => REBINDABLE_ACTIONS.has(action) && isBindableCode(code))
    .map(([action, code]) => `${action}=${code}`)
    .join(',');
}

/**
 * The effective code -> action map: the defaults with the player's choices laid
 * over them.
 *
 * The clearing loop is the whole point. `Input.rebind` alone *adds* a mapping,
 * so "move jump to Z" used to leave Space jumping too — two jump keys, one of
 * which the screen did not mention. Dropping every code that currently produces
 * the action, and only then setting the new one, is what makes the sentence
 * "jump is on Z" true. Taking the chosen code away from whatever else held it
 * follows for the same reason: one key cannot honestly do two things.
 *
 * Generic over the action type so `src/ui` need not import `src/core`; callers
 * pass `DEFAULT_BINDINGS` and get the same value type back.
 */
export function resolveBindings<A extends string>(
  defaults: Readonly<Record<string, A>>,
  overrides: BindOverrides,
): Record<string, A> {
  const out: Record<string, A> = { ...defaults };
  for (const [action, code] of Object.entries(overrides)) {
    if (!REBINDABLE_ACTIONS.has(action)) continue;
    if (!isBindableCode(code)) continue;
    for (const c of Object.keys(out)) if ((out[c] as string) === action) delete out[c];
    out[code] = action as A;
  }
  return out;
}

// ------------------------------------------------------------ pad rebinding
//
// Issue #191. Gamepad buttons were left off this screen for the reason the
// old omissions footnote gave: the rows above capture a keystroke, which a
// pad has none of, and there was no path that applied a captured button to
// the live `Input` anyway. `Input.rebindPad`/`unbindPad` are that path now,
// and the shape below is deliberately the same one `RESERVED_CODES` /
// `REBINDABLE` / `resolveBindings` already use for keys — a second table
// mirroring the first rather than a parallel design, so the two rebinding
// systems read as one feature offered twice, not two.

/**
 * Pad buttons the settings screen refuses to capture into an action binding.
 *
 * Mirrors `MENU_PAD_BUTTONS`' keys in `ui/menuPad.ts` — the buttons that drive
 * the settings screen itself (and every shell menu behind it) plus the d-pad,
 * which already doubles as the menu's up/down/left/right. Binding an action
 * onto one of these would not conflict with the live `Input` (menu and run are
 * mutually exclusive readers of the pad — see `menuPad.ts`'s file header), but
 * it would conflict with *this screen*, the same way `RESERVED_CODES` keeps
 * Tab and Escape off the keyboard rows: the row asking for the button and the
 * button it captured would fight over what a press means while the capture UI
 * itself is still open. Kept as a literal rather than an import, the same
 * reason `padDeadzone`'s default mirrors `core/input.ts`'s `PAD_DEADZONE` (see
 * this file's header) — `tests/menuPad.test.ts` cross-checks the two lists so
 * they cannot drift apart silently.
 *
 * Button 3 joined in issue #193, when Y picked up `reroll`. It only means that
 * on `runSetup`, but the mirror is a blanket policy on purpose — reserving it
 * everywhere a menu might read it is simpler and safer than tracking which
 * shell screen each menu button happens to matter on, and it costs nothing:
 * `interact`, the action Y defaults to, is still reachable on every other free
 * button.
 */
export const RESERVED_PAD_BUTTONS: readonly number[] = [0, 1, 2, 3, 4, 5, 9, 12, 13, 14, 15];

/** Can this pad button be bound to an action? The pad twin of `isBindableCode`. */
export function isBindablePadButton(button: number): boolean {
  return (
    Number.isInteger(button) &&
    button >= 0 &&
    button <= 15 &&
    !RESERVED_PAD_BUTTONS.includes(button)
  );
}

/** action -> the button the player chose for it. Absent means "the default". */
export type PadBindOverrides = Readonly<Record<string, number>>;

/** The `ProfileData.settings` key the pad overrides ride in. */
const PAD_BINDS_KEY = 'input.padBinds';

/** Same totality rule as `readBindOverrides`: anything unparseable is dropped. */
export function readPadBindOverrides(
  raw: Readonly<Record<string, string | number | boolean>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  const packed = raw[PAD_BINDS_KEY];
  if (typeof packed !== 'string') return out;
  for (const pair of packed.split(',')) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue;
    const action = pair.slice(0, eq);
    const button = Number(pair.slice(eq + 1));
    if (!REBINDABLE_ACTIONS.has(action)) continue;
    if (!isBindablePadButton(button)) continue;
    out[action] = button;
  }
  return out;
}

/** Write in place, same rule as `writeBindOverrides`. */
export function writePadBindOverrides(
  overrides: PadBindOverrides,
  into: Record<string, string | number | boolean>,
): void {
  into[PAD_BINDS_KEY] = Object.entries(overrides)
    .filter(([action, button]) => REBINDABLE_ACTIONS.has(action) && isBindablePadButton(button))
    .map(([action, button]) => `${action}=${button}`)
    .join(',');
}

/**
 * The effective button -> action map. The pad twin of `resolveBindings`, and
 * for the same reason: `Input.rebindPad` alone *adds*, so the clearing loop
 * below is what makes "mainAttack is on button 3" actually true rather than
 * leaving button 2 firing it as well.
 */
export function resolvePadBindings<A extends string>(
  defaults: Readonly<Record<number, A>>,
  overrides: PadBindOverrides,
): Record<number, A> {
  const out: Record<number, A> = { ...defaults };
  for (const [action, button] of Object.entries(overrides)) {
    if (!REBINDABLE_ACTIONS.has(action)) continue;
    if (!isBindablePadButton(button)) continue;
    for (const b of Object.keys(out)) if ((out[Number(b)] as string) === action) delete out[Number(b)];
    out[button] = action as A;
  }
  return out;
}

export interface PadBindingRow {
  action: string;
  label: string;
  /** The resolved button index, or null when nothing produces this action any more. */
  button: number | null;
  /** What is shown for that button — or 'unbound'. */
  key: string;
}

/** One row per pad-rebindable action. The pad twin of `bindingRows`. */
export function padBindingRows<A extends string>(
  defaults: Readonly<Record<number, A>>,
  overrides: PadBindOverrides,
): PadBindingRow[] {
  const resolved = resolvePadBindings(defaults, overrides);
  return REBINDABLE.map((r) => {
    const found = Object.keys(resolved).find((b) => (resolved[Number(b)] as string) === r.action);
    const button = found === undefined ? null : Number(found);
    return { action: r.action, label: r.label, button, key: padButtonLabel(button) };
  });
}

/** What is printed for a captured button. The pad twin of `keyLabel`. */
export function padButtonLabel(button: number | null): string {
  return button === null ? 'unbound' : `Button ${button}`;
}

export interface BindingRow {
  action: string;
  label: string;
  /** The resolved code, or null when nothing produces this action any more. */
  code: string | null;
  /** What is written on that key — or 'unbound'. */
  key: string;
}

/**
 * One row per rebindable action, showing the key it *actually* resolves to.
 *
 * Reading the answer back out of the resolved map rather than out of the
 * override list is deliberate: binding jump onto J takes J away from the main
 * attack, and the main-attack row has to say so. A screen that kept showing the
 * old key would be the same lie the whole feature was held back to avoid.
 */
export function bindingRows<A extends string>(
  defaults: Readonly<Record<string, A>>,
  overrides: BindOverrides,
): BindingRow[] {
  const resolved = resolveBindings(defaults, overrides);
  return REBINDABLE.map((r) => {
    const code = Object.keys(resolved).find((c) => (resolved[c] as string) === r.action) ?? null;
    return { action: r.action, label: r.label, code, key: keyLabel(code) };
  });
}

/** What is printed on the key, from its `KeyboardEvent.code`. */
export function keyLabel(code: string | null): string {
  if (!code) return 'unbound';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return 'Num ' + code.slice(6);
  if (code.startsWith('Arrow')) return code.slice(5);
  const sided = /^(Shift|Control|Alt|Meta)(Left|Right)$/.exec(code);
  if (sided) return `${sided[2]} ${sided[1] === 'Control' ? 'Ctrl' : sided[1]}`;
  return code;
}

/**
 * The scale that is *not* a scale: what `--ui-fs` reads at the default option.
 *
 * It is *declared* in `src/ds/tokens.ts` and re-exported here, because the
 * denominator of `--ui-u` is a fact about the design system's scale ladder
 * rather than about this game's settings screen, and the design system may not
 * import from the application (docs/28 §2). Everything that used to read it
 * from this module still can; there is still exactly one `12`.
 */
export { UI_FS_BASE_PX };

/** The `--ui-fs` value each scale option maps to, in CSS pixels. */
export function uiScalePx(value: string): number {
  switch (value) {
    case 'small':
      return 11;
    case 'large':
      return 15;
    case 'huge':
      return 18;
    default:
      return UI_FS_BASE_PX;
  }
}

/**
 * The unitless multiplier the same option produces — `--ui-u`.
 *
 * **`--ui-fs` is canonical.** This is not a second thing the caller has to
 * write: `style.ts` declares `--ui-u: calc(var(--ui-fs) / ${UI_FS_BASE_PX}px)`
 * on `.ui-root`, so the browser derives it from the one property `main.ts` sets
 * and the two cannot disagree. CSS has no way to divide a length back out to a
 * plain number *at authoring time*, but `calc()` can do it at compute time —
 * `calc(18px / 12px)` is the number 1.5 — and that is the whole trick.
 *
 * This function exists so the arithmetic is testable without a browser, and so
 * the ratio has a name. It is the same number the sheet computes, by
 * construction: both divide `uiScalePx(value)` by `UI_FS_BASE_PX`.
 */
export function uiScaleU(value: string): number {
  return uiScalePx(value) / UI_FS_BASE_PX;
}

/**
 * The high-contrast palette: a **theme**, in the design system's sense — an
 * override of a subset of the tokens on the same element that declares them,
 * so it wins by specificity and every surface picks it up on the next paint
 * without knowing the theme exists (docs/28 §6.1).
 *
 * Every key must name a token `src/ds/tokens.ts` actually declares, and
 * `tests/designSystem.test.ts` checks that against the table rather than
 * trusting this comment. It was not true before docs/28: `--ui-fg-inv` was
 * declared *only* here, read by nothing, and overriding nothing — a theme entry
 * for a token that did not exist. It is `--ui-ink-inv` now, which does, and
 * which `::selection` reads.
 */
export const CONTRAST_VARS: Readonly<Record<string, string>> = {
  '--ui-fg': '#ffffff',
  '--ui-fg-dim': '#ddd9d1',
  '--ui-fg-mute': '#aeaaa1',
  '--ui-line': '#6c7686',
  '--ui-line-hot': '#a6b6cc',
  '--ui-panel': '#000208',
  '--ui-panel-2': '#05070e',
  '--ui-panel-3': '#0c1018',
  '--ui-scrim': 'rgba(0,0,0,.93)',
  '--ui-accent': '#ffc470',
  '--ui-ink-inv': '#000208',
};
